const SCI_ROOT = 'https://scriptures.byu.edu/';
const CHURCH_ROOT =
  'https://www.churchofjesuschrist.org/study/scriptures';

const CHURCH_PATHS: Record<string, string> = {
  '101': 'ot/gen',
  '102': 'ot/ex',
  '103': 'ot/lev',
  '104': 'ot/num',
  '105': 'ot/deut',
  '106': 'ot/josh',
  '107': 'ot/judg',
  '108': 'ot/ruth',
  '109': 'ot/1-sam',
  '110': 'ot/2-sam',
  '111': 'ot/1-kgs',
  '112': 'ot/2-kgs',
  '113': 'ot/1-chr',
  '114': 'ot/2-chr',
  '115': 'ot/ezra',
  '116': 'ot/neh',
  '117': 'ot/esth',
  '118': 'ot/job',
  '119': 'ot/ps',
  '120': 'ot/prov',
  '121': 'ot/eccl',
  '122': 'ot/song',
  '123': 'ot/isa',
  '124': 'ot/jer',
  '125': 'ot/lam',
  '126': 'ot/ezek',
  '127': 'ot/dan',
  '128': 'ot/hosea',
  '129': 'ot/joel',
  '130': 'ot/amos',
  '131': 'ot/obad',
  '132': 'ot/jonah',
  '133': 'ot/micah',
  '134': 'ot/nahum',
  '135': 'ot/hab',
  '136': 'ot/zeph',
  '137': 'ot/hag',
  '138': 'ot/zech',
  '139': 'ot/mal',
  '140': 'nt/matt',
  '141': 'nt/mark',
  '142': 'nt/luke',
  '143': 'nt/john',
  '144': 'nt/acts',
  '145': 'nt/rom',
  '146': 'nt/1-cor',
  '147': 'nt/2-cor',
  '148': 'nt/gal',
  '149': 'nt/eph',
  '150': 'nt/philip',
  '151': 'nt/col',
  '152': 'nt/1-thes',
  '153': 'nt/2-thes',
  '154': 'nt/1-tim',
  '155': 'nt/2-tim',
  '156': 'nt/titus',
  '157': 'nt/philem',
  '158': 'nt/heb',
  '159': 'nt/james',
  '160': 'nt/1-pet',
  '161': 'nt/2-pet',
  '162': 'nt/1-jn',
  '163': 'nt/2-jn',
  '164': 'nt/3-jn',
  '165': 'nt/jude',
  '166': 'nt/rev',
  '205': 'bofm/1-ne',
  '206': 'bofm/2-ne',
  '207': 'bofm/jacob',
  '208': 'bofm/enos',
  '209': 'bofm/jarom',
  '210': 'bofm/omni',
  '211': 'bofm/w-of-m',
  '212': 'bofm/mosiah',
  '213': 'bofm/alma',
  '214': 'bofm/hel',
  '215': 'bofm/3-ne',
  '216': 'bofm/4-ne',
  '217': 'bofm/morm',
  '218': 'bofm/ether',
  '219': 'bofm/moro',
  '302': 'dc-testament/dc',
  '401': 'pgp/moses',
  '402': 'pgp/abr',
  '404': 'pgp/js-m',
  '405': 'pgp/js-h',
  '406': 'pgp/a-of-f',
};

export function parseVerseSelection(selection: string): number[] {
  const verses = new Set<number>();
  if (!/^\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*$/.test(selection)) {
    throw new Error('Verse selection is not in canonical form.');
  }
  for (const member of selection.split(',')) {
    if (member.includes('-')) {
      const [startText, endText] = member.split('-');
      const start = Number(startText);
      const end = Number(endText);
      if (
        !Number.isSafeInteger(start) ||
        !Number.isSafeInteger(end) ||
        start <= 0 ||
        end < start ||
        end - start > 300
      ) {
        throw new Error('Verse range is invalid.');
      }
      for (let verse = start; verse <= end; verse += 1) verses.add(verse);
    } else {
      const verse = Number(member);
      if (!Number.isSafeInteger(verse) || verse <= 0) {
        throw new Error('Verse number is invalid.');
      }
      verses.add(verse);
    }
  }
  return [...verses].sort((first, second) => first - second);
}

export function compactVerseSelection(verses: number[]): string {
  const ordered = [...new Set(verses)].sort((first, second) => first - second);
  if (ordered.length === 0 || ordered.some((verse) => verse <= 0)) {
    throw new Error('At least one positive verse is required.');
  }
  const members: string[] = [];
  let start = ordered[0];
  let end = start;
  for (const verse of ordered.slice(1)) {
    if (verse === end + 1) {
      end = verse;
      continue;
    }
    members.push(start === end ? String(start) : `${String(start)}-${String(end)}`);
    start = verse;
    end = verse;
  }
  members.push(start === end ? String(start) : `${String(start)}-${String(end)}`);
  return members.join(',');
}

function encodeSciSelection(selection: string): string {
  let encoded = '';
  for (let index = 0; index < selection.length; index += 1) {
    const character = selection[index];
    const next = selection[index + 1];
    if ((character === '-' || character === ',') && /\d/.test(next)) {
      const base = character === '-' ? 'a'.charCodeAt(0) : 'A'.charCodeAt(0);
      encoded += String.fromCharCode(base + Number(next));
      index += 1;
    } else {
      encoded += character;
    }
  }
  return encoded;
}

export function sciScriptureUrl(
  bookId: string,
  chapter: number,
  verses: number[],
): string {
  const selection = compactVerseSelection(verses);
  const encodedBook = Number(bookId).toString(16).padStart(3, '0');
  const encodedChapter = chapter.toString(16).padStart(2, '0');
  return `${SCI_ROOT}#::c${encodedBook}${encodedChapter}${encodeSciSelection(selection)}`;
}

export function officialChurchScriptureUrl(
  bookId: string,
  chapter: number,
  verses: number[],
): string {
  const path = CHURCH_PATHS[bookId];
  if (!path) throw new Error(`No official Church path exists for SCI book ${bookId}.`);
  const selection = compactVerseSelection(verses);
  const ids = selection
    .split(',')
    .map((member) => {
      if (!member.includes('-')) return `p${member}`;
      const [start, end] = member.split('-');
      return `p${start}-p${end}`;
    })
    .join(',');
  return `${CHURCH_ROOT}/${path}/${String(chapter)}?lang=eng&id=${ids}#p${String(verses[0])}`;
}

export function scriptureRoute(
  bookId: string,
  chapter: number,
  verses: number[],
): string {
  return `/scripture/${encodeURIComponent(bookId)}/${String(chapter)}/${compactVerseSelection(verses)}`;
}
